import { Reducer } from 'interfaces'
import { Notification, NotificationsAppState } from 'interfaces/notifications'
import { ADD_NOTIFICATION, DISMISS_NOTIFICATION, NOTIFICATIONS_SHOWN, TOGGLE_NOTIFICATIONS_EXPANDED } from 'actions/notificationsActions'
import { PAGE_CHANGED } from 'actions/globalActions'
import { createLiveUpdateNotification } from 'helpers/notificationsHelpers'
import { DELETE_MONEYFLOW_TRACKER } from 'actions/moneyflowActions'
import { isSuccessAction } from 'helpers/actionsHelpers'
import { INSIGHT_DISCARD_TRIGGERS, needDiscardData } from 'helpers/discardTriggerHelpers'
import { FLUSH_LIVE_UPDATE, POLL_LIVE_UPDATE } from 'actions/linkageActions'

export const DEFAULT_NOTIFICATIONS_STATE: NotificationsAppState = {
  isExpanded: false,
  notifications: [],
}

export const removeLiveUpdateNotification = (state: NotificationsAppState): NotificationsAppState => {
  const nextNotifications = state.notifications.filter((item) => item.type !== 'live-update')

  return nextNotifications.length < state.notifications.length
    ? {
      ...state,
      notifications: nextNotifications,
    }
    : state
}

const notificationsReducer: Reducer<NotificationsAppState> = (state = DEFAULT_NOTIFICATIONS_STATE, action) => {
  // may collapse when insights are reset
  if (needDiscardData(INSIGHT_DISCARD_TRIGGERS)(action)) {
    const nextIsExpanded = state.notifications.some((notification) => notification.showMode === 'regular')
      ? state.isExpanded
      : false

    state = {
      ...state,
      isExpanded: nextIsExpanded,
    }
  }

  // TODO: test
  if (action.type === POLL_LIVE_UPDATE && isSuccessAction(action)) {
    const { response, params } = action.payload
    const { total_count, failed_count } = response

    const fromStartedToFlushed = params.info && params.info.status === 'started'
      && response.status === 'flushed'

    if (
      response.status === 'buffered' && (total_count === 0 || failed_count === total_count)
      || fromStartedToFlushed
    ) {
      return removeLiveUpdateNotification(state)
    }

    if (
      (response.status === 'started' && (total_count === 0 || params.isRepeatedRequest))
      || response.status === 'flushed'
    ) {
      return state
    }


    // If already added notification is updating
    let hasLiveUpdateNotification = false
    // TODO: rewrite notification to normalized structure to avoid this kind of filtering
    let nextNotifications = state.notifications.reduce<Notification[]>((notifications, notification) => {
      if (notification.type === 'live-update') {
        const liveUpdateNotification = createLiveUpdateNotification(response, notification)
        if (liveUpdateNotification) {
          notifications.push(liveUpdateNotification)
        }
        hasLiveUpdateNotification = true
      } else {
        notifications.push(notification)
      }
      return notifications
    }, [])

    // If new notification is creating
    if (!hasLiveUpdateNotification) {
      const liveUpdateNotification = createLiveUpdateNotification(response)
      nextNotifications = liveUpdateNotification
        ? state.notifications.concat(liveUpdateNotification)
        : state.notifications
    }

    return { ...state, notifications: nextNotifications }
  }

  if (action.type === FLUSH_LIVE_UPDATE && isSuccessAction(action)) {
    return removeLiveUpdateNotification(state)
  }

  switch (action.type) {
    case ADD_NOTIFICATION: {
      const notification = action.payload
      const filteredNotifications = notification.type === 'tracker'
        ? state.notifications.filter(
          (item) => item.type !== 'tracker' || item.creationData.category.id !== notification.creationData.category.id
        )
        : state.notifications
      const nextNotifications = filteredNotifications.concat(notification)

      return {
        ...state,
        notifications: nextNotifications,
      }
    }

    case DISMISS_NOTIFICATION: {
      const nextNotifications = state.notifications.filter((item) => item.id !== action.payload)

      return {
        ...state,
        notifications: nextNotifications,
      }
    }

    case NOTIFICATIONS_SHOWN: {
      let freshNotificationCount = 0
      const nextNotifications = state.notifications.map((notification): Notification => {
        if (notification.showMode === 'fresh') {
          freshNotificationCount++
          return {
            ...notification,
            showMode: 'regular',
            prevShowMode: notification.showMode,
          }
        }

        return notification
      })

      return freshNotificationCount
        ? {
          ...state,
          notifications: nextNotifications,
        }
        : state
    }

    case TOGGLE_NOTIFICATIONS_EXPANDED: {
      return {
        ...state,
        isExpanded: action.payload.isExpanded,
      }
    }

    case PAGE_CHANGED: {
      if (action.payload.prev === null) {
        return state
      }

      // leave only "live update is ready" notifications
      const nextNotifications = state.notifications.filter((item) => item.type === 'live-update')
      return {
        ...DEFAULT_NOTIFICATIONS_STATE,
        notifications: nextNotifications,
      }
    }

    case DELETE_MONEYFLOW_TRACKER: {
      if (!isSuccessAction(action)) {
        return state
      }

      const trackerId = action.payload.params.tracker.id
      const nextNotifications = state.notifications.filter((item) =>
        item.type !== 'tracker' || item.creationData.id !== trackerId)

      return {
        ...state,
        notifications: nextNotifications,
      }
    }

    default:
      return state
  }
}

export default notificationsReducer
